import { CSSProperties, ReactNode } from 'react';

import {
  CurrentClientQuery,
  CurrentSessionQuery,
  UserFeatureAccessType,
} from '@/generated';
import { checkAccess } from '@/providers/UserPermissionsProvider/hooks/rbacFallback';
import theme from '@/theme';
import { isFeatureAccessible } from '@/utils/app-utils';
import { PERMISSIONS, Permission } from '@/utils/permissionTypes';
import { UserRole } from '@/utils/roleUtils';

export type NavItem = {
  id: string;
  title: ReactNode;
  titleDecoration?: string;
  header?: string;
  domains: Domains | null;
  isStaff: boolean;
  route?: string;
  iconName?: string;
  subItems?: NavItem[];
  isExternal?: boolean;
  hasFeatures?: string[];
  featureAccess: UserFeatureAccessType;
  shouldSwitchClients?: boolean;
  visibilityCriteria?: boolean;
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove requiredRole when FF is fully rolled out
  requiredRole?: UserRole;
  requiredPermission?: Permission;
  action?: () => void;
};

export type CurrentClientType = CurrentClientQuery['currentClient'];
export type Domains = {
  prod: string;
  local?: string;
  staging?: string;
  dev?: string;
};

export const SPA_DOMAINS: Domains = {
  staging: 'https://staging2.coherentpath.com',
  prod: 'https://app2.coherentpath.com',
  local: import.meta.env.VITE_SPA_URL,
  dev: 'https://app.dev.cp.gcp.movableink.dev',
};

export const LEGACY_DOMAINS: Domains = {
  staging: 'https://staging.coherentpath.com',
  prod: 'https://app.coherentpath.com',
  dev: 'https://app.dev.cp.gcp.movableink.dev',
  local: import.meta.env.VITE_LEGACY_URL,
};

type AccessFilterOptions = {
  currentClient: CurrentClientType | undefined;
  currentUser: CurrentSessionQuery['currentUser'] | undefined;
  navItem: NavItem;
  hasAccountPermissions: boolean;
  hasPermission: (permission: string) => boolean;
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove hasRole when FF is fully rolled out
  hasRole: (role: UserRole) => boolean;
};

export const accessFilter = ({
  currentClient,
  currentUser,
  navItem,
  hasAccountPermissions,
  hasPermission,
  hasRole,
}: AccessFilterOptions) => {
  if (!currentUser || !navItem) return false;

  const hasFeatureAccess =
    navItem.hasFeatures?.some(
      (feature) => currentClient?.[feature as keyof CurrentClientType] === true,
    ) ?? true;

  const shouldNavItemBeVisible = navItem.visibilityCriteria ?? true;

  if (!hasFeatureAccess || !shouldNavItemBeVisible) return false;

  const hasStaffAccess =
    !navItem.isStaff || currentUser?.isStaff === navItem.isStaff;

  // TODO: feature_flag_cleanup: hasAccountPermissions - Replace with just hasPermission(navItem.requiredPermission) when FF is fully rolled out
  const hasAccess = checkAccess({
    permission: navItem.requiredPermission,
    role: navItem.requiredRole,
    hasAccountPermissions,
    hasPermission,
    hasRole,
  });

  return (
    hasStaffAccess &&
    hasAccess &&
    isFeatureAccessible(currentUser?.featureAccess, navItem.featureAccess)
  );
};

export const getEnv = () => {
  const baseDomain = window.location.hostname;
  if (baseDomain.includes('-dev')) return 'dev';
  return baseDomain.includes('-stg') ||
    baseDomain.includes('ink-ui-test--preview')
    ? 'staging'
    : baseDomain.includes('localhost') || baseDomain.includes('127.0.0.1')
      ? 'local'
      : 'prod';
};

export const getUrl = (
  clientId: string,
  route: string,
  domains: Domains | null,
  stripClientId = false,
  shouldSwitchClients = false,
) => {
  const env = getEnv();
  if (shouldSwitchClients && domains) {
    return `${domains[env]}/switch_client/?next=${encodeURIComponent(
      route,
    )}&client_id=${clientId}`;
  } else if (stripClientId && domains) {
    return `${domains[env]}${route}`;
  } else {
    return domains && env !== 'local'
      ? `${domains[env]}/${clientId}${route}`
      : `${clientId}${route}`;
  }
};

export const navItemActiveStyling = {
  backgroundColor: theme.primaryNav.activeBackgrounColor,
  borderRadius: theme.primaryNav.itemBorderRadius,
} as const satisfies CSSProperties;

export const navItemHoverStyling = {
  backgroundColor: theme.primaryNav.hoverBackgroundColor,
  borderRadius: theme.primaryNav.itemBorderRadius,
} as const satisfies CSSProperties;

export const navItemTransition = (isDrawerOpen: boolean): CSSProperties => ({
  opacity: isDrawerOpen ? 1 : 0,
  transition: `opacity ${theme.transitions.duration.complex}ms ${theme.transitions.easing.sharp}`,
});

export const getMessagingMenuOptions = (hasPrograms: boolean): NavItem[] => {
  return [
    {
      id: 'messaging',
      title: 'Messaging',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: hasPrograms,
      requiredPermission: PERMISSIONS.CAMPAIGNS_READ,
      subItems: [
        {
          id: 'campaigns',
          title: 'Marketing Calendar',
          route: `/campaigns`,
          domains: null,
          featureAccess: UserFeatureAccessType.Release,
          isStaff: false,
          requiredPermission: PERMISSIONS.CAMPAIGNS_READ,
        },
        {
          id: 'campaigns-list',
          title: 'Campaigns',
          route: '/campaignslist',
          domains: null,
          featureAccess: UserFeatureAccessType.Release,
          isStaff: false,
          requiredPermission: PERMISSIONS.CAMPAIGNS_READ,
        },

        {
          id: 'programs',
          title: 'Agentic Campaigns',
          route: '/programs',
          domains: null,
          featureAccess: UserFeatureAccessType.Release,
          isStaff: false,
          requiredPermission: PERMISSIONS.CAMPAIGNS_READ,
        },
      ],
    },
    {
      id: 'campaigns',
      title: 'Campaigns',
      route: `/campaigns`,
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: !hasPrograms,
      requiredPermission: PERMISSIONS.CAMPAIGNS_READ,
    },
  ];
};

// Analytics sub-items kept in the nav when hasUnifiedMessagingNav is on. Later
// sections (Content, Journeys, Audiences) are added here as they're built.
// TODO: feature_flag_cleanup: hasUnifiedMessagingNav
const UNIFIED_ANALYTICS_SUB_ITEM_IDS = [
  'analytics-dashboard',
  'analytics-campaigns-v2',
];

// Replaces Creatives and Subject Lines in the unified nav with one tabbed page
// (Creatives, Subject Lines, Links). Not part of the old nav.
// TODO: feature_flag_cleanup: hasUnifiedMessagingNav
const CONTENT_NAV_ITEM: NavItem = {
  id: 'analytics-content',
  title: 'Content',
  route: '/analytics/content',
  domains: null,
  featureAccess: UserFeatureAccessType.Release,
  isStaff: false,
  requiredPermission: PERMISSIONS.ANALYTICS_READ,
};

// Replaces Experiments and Segment Groups in the unified nav with one tabbed
// page. Not part of the old nav.
// TODO: feature_flag_cleanup: hasUnifiedMessagingNav
const AUDIENCES_NAV_ITEM: NavItem = {
  id: 'analytics-audiences',
  title: 'Audiences',
  route: '/analytics/audiences',
  domains: null,
  featureAccess: UserFeatureAccessType.Release,
  isStaff: false,
  requiredPermission: PERMISSIONS.ANALYTICS_READ,
};

type AnalyticsNavOptions = {
  hasPrograms: boolean;
  hasReportingDashboard: boolean;
  // TODO: feature_flag_cleanup: hasUnifiedMessagingNav
  hasUnifiedMessagingNav: boolean;
};

export const getAnalyticsNavItems = ({
  hasPrograms,
  hasReportingDashboard,
  hasUnifiedMessagingNav,
}: AnalyticsNavOptions): NavItem[] => {
  const subItems: NavItem[] = [
    {
      id: 'analytics-dashboard',
      title: 'Dashboard',
      route: '/analytics/dashboard',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: hasReportingDashboard,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
    {
      id: 'analytics-programs',
      title: 'Programs',
      route: '/analytics/programs',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      visibilityCriteria: hasPrograms && false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
    {
      id: 'analytics-campaigns-v2',
      title: 'Campaigns',
      route: '/analytics/campaigns-v2',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
    {
      id: 'analytics-creatives-v2',
      title: 'Creatives',
      route: '/analytics/creatives-v2',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
    {
      id: 'analytics-subject-lines-v2',
      title: 'Subject Lines',
      route: '/analytics/subject-lines-v2',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
    {
      id: 'analytics-segments-v2',
      title: 'Segment Groups',
      route: '/analytics/segments-v2',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
    {
      id: 'analytics-experiments-v2',
      title: 'Experiments',
      route: '/analytics/experiments-v2',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
    },
  ];

  return [
    {
      id: 'analytics-v2',
      title: 'Analytics',
      domains: null,
      featureAccess: UserFeatureAccessType.Release,
      isStaff: false,
      requiredPermission: PERMISSIONS.ANALYTICS_READ,
      subItems: hasUnifiedMessagingNav
        ? [
            ...subItems.filter(({ id }) =>
              UNIFIED_ANALYTICS_SUB_ITEM_IDS.includes(id),
            ),
            CONTENT_NAV_ITEM,
            AUDIENCES_NAV_ITEM,
          ]
        : subItems,
    },
  ];
};
